import styled from "@emotion/styled";
import {
  createCustomSpacing,
  Grid,
  GridColumn,
  MediaQueries,
  Spacer,
} from "@polestar/component-warehouse-react";
import { type MutableRefObject } from "react";

import { type InPageNavigationLink } from "../../data/generateInPageNavigationLinks";
import { ContentHeader } from "../ContentHeader";
import { InPageNavigation } from "../InPageNavigation";
import { StructuredText } from "../StructuredText";

import { type Maybe } from "~/common/types";
import { type DocumentStructuredText } from "~/types";

export interface ContentWithStructuredTextProps {
  readonly content: DocumentStructuredText[];
  readonly initiallyExpandedAccordionPanels?: Array<{
    parentId: string;
    panels: Record<string, boolean>;
  }>;
  readonly sectionRefs: MutableRefObject<HTMLElement[]>;
  readonly inPageNavigationLinks: Maybe<InPageNavigationLink[]>;
  readonly dictionary: Record<string, string>;
  readonly title: Maybe<string>;
  readonly version: Maybe<string>;
  readonly localizedDate: Maybe<string>;
  readonly changelogDescription: Maybe<string>;
  readonly handleDownloadPdf: () => void;
}

const ContentHeaderGridColumn = styled(GridColumn)`
  order: -1;
  ${MediaQueries.tablet} {
    order: 0;
  }
`;

const StyledGrid = styled(Grid)`
  grid-template-rows: auto auto;
  grid-row-gap: 0;
  ${MediaQueries.tablet} {
    grid-row-gap: 0;
  }
`;

const InPageNavigationGridColumn = styled(GridColumn)`
  height: 100%;
  grid-row-end: span 2;
`;

const hasInPageNavigationLinks = (
  inPageNavigationLinks: Maybe<InPageNavigationLink[]>
): inPageNavigationLinks is InPageNavigationLink[] => {
  return Boolean(inPageNavigationLinks?.length);
};

const ContentWithStructuredText = ({
  content,
  changelogDescription,
  dictionary,
  handleDownloadPdf,
  initiallyExpandedAccordionPanels,
  inPageNavigationLinks,
  localizedDate,
  sectionRefs,
  title,
  version,
}: ContentWithStructuredTextProps) => {
  return (
    <StyledGrid nested>
      {hasInPageNavigationLinks(inPageNavigationLinks) ? (
        <InPageNavigationGridColumn span={{ mobile: 4, tablet: 2, desktop: 4 }}>
          <InPageNavigation
            mobileTitle={dictionary.Index}
            links={inPageNavigationLinks}
            sectionRefs={sectionRefs}
          />
        </InPageNavigationGridColumn>
      ) : (
        <InPageNavigationGridColumn
          span={{ mobile: 0, tablet: 0, desktop: 4 }}
        />
      )}

      <ContentHeaderGridColumn
        span={{
          mobile: 4,
          tablet: hasInPageNavigationLinks(inPageNavigationLinks) ? 8 : 5,
          desktop: 8,
        }}
      >
        <ContentHeader
          title={title || ""}
          version={version || ""}
          date={localizedDate || ""}
          dictionary={dictionary}
          isChangelogAvailable={Boolean(changelogDescription)}
          onDownloadPdf={handleDownloadPdf}
        />
      </ContentHeaderGridColumn>

      <GridColumn
        id="printable"
        span={{
          mobile: 4,
          tablet: hasInPageNavigationLinks(inPageNavigationLinks) ? 8 : 5,
          desktop: 8,
        }}
      >
        {content?.map((section, index) => {
          return (
            <div
              // there is no unique value too use as a key
              key={index}
              ref={(element) => {
                if (element) {
                  sectionRefs.current[`${index}`] = element;
                }
              }}
            >
              <StructuredText
                content={section}
                initiallyExpandedAccordionPanels={
                  initiallyExpandedAccordionPanels
                }
              />
            </div>
          );
        })}
        <Spacer spacing={createCustomSpacing(3.5)} />
      </GridColumn>
    </StyledGrid>
  );
};

export default ContentWithStructuredText;
